import React from 'react';
import { intervalToDuration } from 'date-fns';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import { SurveyMigrationJob } from 'src/api/surveyMigration';
import { usePauseSurveyMigration, useResumeSurveyMigration } from 'src/queries/useSurveyMigration';

interface ProgressSectionProps {
    job: SurveyMigrationJob;
}

function formatElapsed(startedAt: string | null): string {
    if (!startedAt) {
        return '—';
    }
    const start = new Date(startedAt);
    if (Number.isNaN(start.getTime())) {
        return '—';
    }
    const { days = 0, hours = 0, minutes = 0, seconds = 0 } = intervalToDuration({ start, end: new Date() });
    const totalHours = days * 24 + hours;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return totalHours > 0 ? `${totalHours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}

function ProgressSection({ job }: ProgressSectionProps) {
    const pauseMigration = usePauseSurveyMigration();
    const resumeMigration = useResumeSurveyMigration();

    const isPaused = job.status === 'PAUSED';
    const percent = Math.min(100, Math.max(0, job.percentComplete));

    const handlePause = () => pauseMigration.mutate();
    const handleResume = () => resumeMigration.mutate();

    const mutationError = pauseMigration.error ?? resumeMigration.error;

    return (
        <Card className={styles.cardWrap} label="Migration Progress">
            <Flex direction="column" className={styles.sectionBody}>
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <span className={isPaused ? styles.statusPaused : styles.statusActive}>
                        {isPaused ? 'PAUSED' : 'IN PROGRESS'}
                    </span>
                </Flex>

                <div className={styles.progressBarTrack}>
                    <div className={styles.progressBarFill} style={{ width: `${percent}%` }} />
                </div>
                <Flex direction="row" justify="between" className={styles.progressMeta}>
                    <span>
                        {job.processedCount.toLocaleString()} of {job.totalSurveys.toLocaleString()} surveys
                    </span>
                    <span>{percent}%</span>
                </Flex>

                <Flex direction="row" className={styles.progressStats}>
                    <div>
                        <span className={styles.metaLabel}>Failed:</span> {job.failedCount.toLocaleString()}
                    </div>
                    <div>
                        <span className={styles.metaLabel}>Elapsed:</span> {formatElapsed(job.startedAt)}
                    </div>
                </Flex>

                {mutationError && (
                    <div className={styles.error}>
                        {errorMessage(mutationError, 'Failed to update migration. Please try again.')}
                    </div>
                )}

                <Flex direction="row" className={styles.buttonRow}>
                    {isPaused ? (
                        <Button
                            type="primary"
                            text="Resume Migration"
                            isLoading={resumeMigration.isLoading}
                            onClick={handleResume}
                        />
                    ) : (
                        <Button
                            type="outline"
                            text="Pause Migration"
                            isLoading={pauseMigration.isLoading}
                            onClick={handlePause}
                        />
                    )}
                </Flex>
            </Flex>
        </Card>
    );
}

export default ProgressSection;
